import { Component, Inject, OnDestroy, PLATFORM_ID, TemplateRef, ViewChild } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { ScimIssuedToken } from '../../../models/scim';
import { formatUtc } from '../../common-functions';

/**
 * One-time reveal of a freshly issued SCIM bearer token.
 *
 * The plaintext token exists only in this component's `token` field for as long as
 * the dialog is open. It is deliberately never written to local/session storage,
 * a shared store, or the console - once the dialog closes it is gone and the admin
 * has to revoke and generate a new one.
 */
@Component({
    selector: 'app-scim-token-dialog',
    templateUrl: './scim-token-dialog.component.html',
    styleUrl: './scim-token-dialog.component.scss',
    standalone: false
})
export class ScimTokenDialogComponent implements OnDestroy {
    @ViewChild('scimTokenModal', { static: false }) scimTokenModal: TemplateRef<any>;

    token: string | null = null;
    expiresOn: string | null = null;
    rotationDays: number | null = null;
    serverMessage: string | null = null;
    acknowledged = false;

    readonly formatUtc = formatUtc;

    private modalRef: NgbModalRef | null = null;

    constructor(
        @Inject(PLATFORM_ID) private readonly platformId: any,
        private readonly modalService: NgbModal,
        private readonly toastr: ToastrService
    ) { }

    /** Resolves when the reveal is closed, so callers can refresh what changed. */
    open(issued: ScimIssuedToken): Promise<void> {
        if (!issued?.token || !isPlatformBrowser(this.platformId)) {
            return Promise.resolve();
        }
        this.token = issued.token;
        this.expiresOn = issued.expiresOn ?? null;
        this.rotationDays = issued.rotationDays ?? null;
        this.serverMessage = issued.message ?? null;
        this.acknowledged = false;

        this.modalRef = this.modalService.open(this.scimTokenModal, {
            size: 'lg',
            centered: true,
            backdrop: 'static',
            keyboard: false,
            ariaLabelledBy: 'scim-token-title',
            windowClass: 'modal'
        });
        return this.modalRef.result.then(() => this.clear(), () => this.clear());
    }

    async copyToken(): Promise<void> {
        if (!this.token) {
            return;
        }
        try {
            await navigator.clipboard.writeText(this.token);
            this.toastr.success('Token copied to clipboard', 'Copied');
        } catch {
            this.toastr.error('Could not copy the token. Select it and copy it manually.', 'Copy failed');
        }
    }

    selectToken(target: EventTarget | null): void {
        (target as HTMLInputElement)?.select();
    }

    done(): void {
        this.modalRef?.close('acknowledged');
    }

    private clear(): void {
        this.token = null;
        this.expiresOn = null;
        this.rotationDays = null;
        this.serverMessage = null;
        this.acknowledged = false;
        this.modalRef = null;
    }

    ngOnDestroy(): void {
        this.modalRef?.dismiss('destroyed');
        this.clear();
    }
}
